import { DomainError } from '../errors.js';
import {
  criterionKey,
  DEFAULT_PERFORMANCE_CRITERIA,
  type DrillCriterion,
  type DrillFrequency,
  type DrillTemplateContent,
  frequencyMonths,
  frequencyOfMonths,
  isDrillCode,
  parseDrillContent,
} from './template.js';

/**
 * Tatbikat kütüphanesinin Excel biçimi (ADR 0128 §7): tek sayfa `Drill Library`, bir satır bir
 * şablon, başlıklar ortağın belgesi §3'ün alan adları. Çok değerli hücrede her satır bir öğedir
 * (Alt+Enter) ya da ` | ` ile ayrılır; rol "Rol: görev", kriter "Ad (time)" ya da "Ad (rating)".
 * Önizleme ve yazma `packages/db` `importDrillLibrary`'dedir; burada yalnız okuma ve satır hatası.
 */

export type DrillSheetCell = string | number | null;

export const DRILL_LIBRARY_SHEET = 'Drill Library';

export const DRILL_LIBRARY_COLUMNS = [
  { key: 'code', header: 'Drill Code', required: true },
  { key: 'name', header: 'Name', required: true },
  { key: 'category', header: 'Category', required: true },
  { key: 'subcategory', header: 'Subcategory', required: false },
  { key: 'version', header: 'Version', required: false },
  { key: 'active', header: 'Active', required: false },
  { key: 'vesselTypes', header: 'Applicable Vessel Types', required: false },
  { key: 'regulatoryReference', header: 'Flag / Regulatory Reference', required: false },
  { key: 'smsProcedure', header: 'SMS Procedure Link', required: false },
  { key: 'objective', header: 'Objective', required: true },
  { key: 'scenario', header: 'Scenario Description', required: true },
  { key: 'equipment', header: 'Required Equipment', required: false },
  { key: 'roles', header: 'Roles / Participants', required: false },
  { key: 'preparation', header: 'Preparation Checklist', required: false },
  { key: 'steps', header: 'Execution Steps', required: true },
  { key: 'safety', header: 'Safety Precautions', required: false },
  { key: 'criteria', header: 'Performance Criteria', required: false },
  { key: 'debriefing', header: 'Debriefing Questions', required: false },
  { key: 'correctiveActions', header: 'Recommended Corrective Actions', required: false },
  { key: 'reportTemplate', header: 'Report Template', required: false },
  { key: 'frequency', header: 'Frequency', required: true },
  { key: 'ruleCode', header: 'Rule Code', required: false },
] as const;
type ColumnKey = (typeof DRILL_LIBRARY_COLUMNS)[number]['key'];

export type DrillImportIssueCode =
  | 'REQUIRED'
  | 'TOO_LONG'
  | 'TOO_MANY'
  | 'INVALID_CODE'
  | 'INVALID_OPTION'
  | 'INVALID_KEY'
  | 'DUPLICATE_KEY'
  | 'DUPLICATE_ROW'
  | 'OUT_OF_RANGE'
  | 'INVALID_TEXT'
  | 'UNKNOWN_CATEGORY'
  | 'INACTIVE_CATEGORY';

export interface DrillImportIssue {
  /** Excel satır numarası (başlık 1). */
  row: number;
  /** Sütun başlığı. */
  field: string;
  code: DrillImportIssueCode | string;
}

export interface DrillSheetRow {
  row: number;
  code: string;
  name: string;
  category: string;
  subcategory: string | null;
  active: boolean;
  content: DrillTemplateContent;
}

export interface DrillSheetData {
  rows: DrillSheetRow[];
  errors: DrillImportIssue[];
}

const collapse = (value: string) => value.normalize('NFC').trim().replace(/\s+/g, ' ');
const headerKey = (value: DrillSheetCell | undefined) =>
  value === null || value === undefined ? '' : collapse(String(value)).toLowerCase();

function cellText(value: DrillSheetCell | undefined): string | null {
  if (value === null || value === undefined) return null;
  const text = typeof value === 'number' ? String(value) : collapse(value);
  return text === '' ? null : text;
}

/** Uzun metin: satır sonları korunur. */
function cellParagraph(value: DrillSheetCell | undefined): string | null {
  if (value === null || value === undefined) return null;
  const text = String(value)
    .normalize('NFC')
    .replace(/\r\n|\r/g, '\n')
    .trim();
  return text === '' ? null : text;
}

/** Çok değerli hücre: satır sonu ya da ` | `. */
function cellList(value: DrillSheetCell | undefined): string[] {
  if (value === null || value === undefined) return [];
  return String(value)
    .normalize('NFC')
    .split(/\r\n|\r|\n|\s\|\s/)
    .map(collapse)
    .filter((item) => item !== '');
}

const FREQUENCY_WORDS: Readonly<Record<string, number>> = {
  monthly: 1,
  bimonthly: 2,
  'bi-monthly': 2,
  quarterly: 3,
  'semi-annual': 6,
  semiannual: 6,
  'semi-annually': 6,
  'half-yearly': 6,
  annual: 12,
  annually: 12,
  yearly: 12,
};

/** "Monthly", "Quarterly", "Every 2 months", "Custom: 4", "4" → sıklık; okunamazsa `null`. */
export function parseFrequencyText(text: string): DrillFrequency | null {
  const value = text.trim().toLowerCase();
  const word = FREQUENCY_WORDS[value];
  if (word !== undefined) return frequencyOfMonths(word);
  const match = /^(?:custom\s*:?\s*|every\s+)?(\d{1,2})(?:\s*months?)?$/.exec(value);
  const months = match ? Number(match[1]) : Number.NaN;
  return Number.isInteger(months) && months >= 1 && months <= 60 ? frequencyOfMonths(months) : null;
}

/** Sıklığın Excel ve ekran metni (İngilizce; dosya dilden bağımsız). */
export function frequencyText(frequency: DrillFrequency): string {
  switch (frequency.kind) {
    case 'monthly':
      return 'Monthly';
    case 'quarterly':
      return 'Quarterly';
    case 'annual':
      return 'Annual';
    default:
      return `Every ${frequencyMonths(frequency)} months`;
  }
}

const ACTIVE_WORDS: Readonly<Record<string, boolean>> = {
  yes: true,
  y: true,
  true: true,
  '1': true,
  active: true,
  no: false,
  n: false,
  false: false,
  '0': false,
  inactive: false,
};

function criteriaOf(items: string[]): DrillCriterion[] {
  if (items.length === 0) return DEFAULT_PERFORMANCE_CRITERIA.map((c) => ({ ...c }));
  return items.map((item) => {
    const match = /^(.*?)\s*\((time|rating)\)\s*$/i.exec(item);
    const label = collapse(match?.[1] ?? item);
    const kind = (match?.[2]?.toLowerCase() ?? 'rating') as DrillCriterion['kind'];
    return { key: criterionKey(label), label, kind };
  });
}

function rolesOf(items: string[]): { role: string; duty: string | null }[] {
  return items.map((item) => {
    const at = item.indexOf(':');
    if (at < 0) return { role: item, duty: null };
    const duty = collapse(item.slice(at + 1));
    return { role: collapse(item.slice(0, at)), duty: duty === '' ? null : duty };
  });
}

/** Şemanın alan yolu → sütun başlığı. */
const FIELD_OF: Readonly<Record<string, string>> = Object.fromEntries(
  DRILL_LIBRARY_COLUMNS.map((c) => [c.key, c.header]),
);

function columnsOf(header: DrillSheetCell[]): Map<ColumnKey, number> {
  const positions = new Map<string, number>();
  for (const [index, cell] of header.entries()) {
    const key = headerKey(cell);
    if (key !== '' && !positions.has(key)) positions.set(key, index);
  }
  const map = new Map<ColumnKey, number>();
  for (const column of DRILL_LIBRARY_COLUMNS) {
    const at = positions.get(column.header.toLowerCase());
    if (at === undefined) {
      if (column.required) throw new DomainError('INVALID_HEADER', column.header);
      continue;
    }
    map.set(column.key, at);
  }
  return map;
}

/**
 * Sayfayı şablon satırlarına çevirir. Sayfa yoksa `SHEET_MISSING`, gerekli başlık yoksa
 * `INVALID_HEADER` fırlatır (sıra ve büyük/küçük harf serbest). Hatalı satır veriye girmez; aynı
 * kod ikinci kez gelirse ikinci satır `DUPLICATE_ROW`. Kategori adı/kodu burada çözülmez.
 */
export function parseDrillLibrarySheet(rows: DrillSheetCell[][] | null): DrillSheetData {
  if (rows === null) throw new DomainError('SHEET_MISSING', DRILL_LIBRARY_SHEET);
  const [header, ...body] = rows;
  if (!header) throw new DomainError('INVALID_HEADER', DRILL_LIBRARY_SHEET);
  const columns = columnsOf(header);
  const out: DrillSheetData = { rows: [], errors: [] };
  const seen = new Set<string>();
  for (const [index, cells] of body.entries()) {
    const raw = (key: ColumnKey) => {
      const at = columns.get(key);
      return at === undefined ? null : (cells[at] ?? null);
    };
    if (cells.every((c) => cellText(c) === null)) continue;
    const row = index + 2;
    const errors: DrillImportIssue[] = [];
    const error = (key: ColumnKey, code: string) => {
      if (!errors.some((e) => e.field === FIELD_OF[key])) {
        errors.push({ row, field: FIELD_OF[key] ?? key, code });
      }
    };

    const code = cellText(raw('code'))?.toUpperCase() ?? null;
    if (code === null) error('code', 'REQUIRED');
    else if (!isDrillCode(code)) error('code', 'INVALID_CODE');
    else if (seen.has(code)) error('code', 'DUPLICATE_ROW');
    const name = cellText(raw('name'));
    if (name === null) error('name', 'REQUIRED');
    else if (name.length > 120) error('name', 'TOO_LONG');
    const category = cellText(raw('category'));
    if (category === null) error('category', 'REQUIRED');
    const activeText = cellText(raw('active'));
    const active = activeText === null ? true : ACTIVE_WORDS[activeText.toLowerCase()];
    if (active === undefined) error('active', 'INVALID_OPTION');
    const frequencyCell = cellText(raw('frequency'));
    const frequency = frequencyCell === null ? null : parseFrequencyText(frequencyCell);
    if (frequencyCell === null) error('frequency', 'REQUIRED');
    else if (frequency === null) error('frequency', 'INVALID_OPTION');

    const content = parseDrillContent({
      vesselTypes: cellList(raw('vesselTypes')),
      regulatoryReference: cellText(raw('regulatoryReference')),
      smsProcedure: cellText(raw('smsProcedure')),
      objective: cellParagraph(raw('objective')) ?? '',
      scenario: cellParagraph(raw('scenario')) ?? '',
      equipment: cellList(raw('equipment')),
      roles: rolesOf(cellList(raw('roles'))),
      preparation: cellList(raw('preparation')),
      steps: cellList(raw('steps')),
      safety: cellList(raw('safety')),
      criteria: criteriaOf(cellList(raw('criteria'))),
      debriefing: cellList(raw('debriefing')),
      correctiveActions: cellList(raw('correctiveActions')),
      reportTemplate: cellParagraph(raw('reportTemplate')),
      frequency: frequency ?? { kind: 'monthly', months: null },
      ruleCode: cellText(raw('ruleCode'))?.toUpperCase() ?? null,
    });
    if (!content.ok) {
      for (const issue of content.issues) {
        const key = issue.path.split('.')[0] as ColumnKey;
        error(FIELD_OF[key] ? key : 'objective', issue.code);
      }
    }
    if (code !== null && !errors.some((e) => e.code === 'DUPLICATE_ROW')) seen.add(code);
    if (errors.length > 0 || !content.ok) {
      out.errors.push(...errors);
      continue;
    }
    out.rows.push({
      row,
      code: code as string,
      name: name as string,
      category: category as string,
      subcategory: cellText(raw('subcategory')),
      active: active as boolean,
      content: content.value,
    });
  }
  return out;
}

/** Dışa aktarılan şablon (kategori adlarıyla). */
export interface DrillExportTemplate {
  code: string;
  name: string;
  category: string;
  subcategory: string | null;
  version: number;
  active: boolean;
  content: DrillTemplateContent;
}

/** Şablonları sayfa satırlarına çevirir (başlık hariç); dosya doldurulup geri yüklenebilir. */
export function drillLibraryRowsOf(templates: readonly DrillExportTemplate[]): DrillSheetCell[][] {
  const lines = (items: readonly string[]) => (items.length ? items.join('\n') : null);
  return templates.map((t) => {
    const c = t.content;
    const values: Record<ColumnKey, DrillSheetCell> = {
      code: t.code,
      name: t.name,
      category: t.category,
      subcategory: t.subcategory,
      version: t.version,
      active: t.active ? 'Yes' : 'No',
      vesselTypes: lines(c.vesselTypes),
      regulatoryReference: c.regulatoryReference,
      smsProcedure: c.smsProcedure,
      objective: c.objective,
      scenario: c.scenario,
      equipment: lines(c.equipment),
      roles: lines(c.roles.map((r) => (r.duty ? `${r.role}: ${r.duty}` : r.role))),
      preparation: lines(c.preparation),
      steps: lines(c.steps),
      safety: lines(c.safety),
      criteria: lines(c.criteria.map((k) => `${k.label} (${k.kind})`)),
      debriefing: lines(c.debriefing),
      correctiveActions: lines(c.correctiveActions),
      reportTemplate: c.reportTemplate,
      frequency: frequencyText(c.frequency),
      ruleCode: c.ruleCode,
    };
    return DRILL_LIBRARY_COLUMNS.map((column) => values[column.key]);
  });
}
